/**
 * Boozy Cakes — Product page behaviours
 * - Gallery thumbs
 * - Description read more / less
 * - Quantity steppers
 * - Variant selection
 * - Main ATC + bundle quick-add via /cart/add.js
 * - Dawn-compatible cart-drawer / cart-notification refresh
 */
(function () {
  'use strict';

  const MONEY_FORMAT = window.Shopify && window.Shopify.money_format;

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && MONEY_FORMAT) {
      return window.Shopify.formatMoney(cents, MONEY_FORMAT);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return '$' + amount;
  }

  function parseProductJson(root) {
    const el = root.querySelector('[data-bzc-product-json]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function getVariantFromOptions(product, selectedOptions) {
    if (!product || !product.variants) return null;
    return product.variants.find(function (variant) {
      return variant.options.every(function (opt, i) {
        return opt === selectedOptions[i];
      });
    });
  }

  function getCartUi() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function appendCartSections(formData) {
    const cart = getCartUi();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart
          .getSectionsToRender()
          .map(function (section) {
            return section.id;
          })
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      return;
    }

    // Dawn fallback section IDs
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);
  }

  async function addToCartFromFormData(formData) {
    appendCartSections(formData);

    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartAddUrl =
      (window.routes && window.routes.cart_add_url) || rootPath + 'cart/add.js';

    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add to cart');
    }
    return data;
  }

  async function addVariantToCart(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(Math.max(1, Number(quantity) || 1)));
    return addToCartFromFormData(formData);
  }

  function publishCartUpdate(response) {
    const cart = getCartUi();

    if (cart && response && typeof cart.renderContents === 'function') {
      // Matches theme cart-drawer.js behavior
      const drawerInner = cart.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.classList.remove('is-empty');
      cart.classList.remove('is-empty');
      cart.renderContents(response);
    } else if (cart && typeof cart.open === 'function') {
      cart.open();
    } else if (!cart && window.routes && window.routes.cart_url) {
      window.location = window.routes.cart_url;
      return;
    }

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'bzc-product-form',
        cartData: response
      });
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('bzc:cart:updated', { detail: response }));
    if (typeof window.refreshCart === 'function') window.refreshCart();
  }

  function initReadMore(root) {
    const desc = root.querySelector('[data-bzc-desc]');
    if (!desc) return;

    const body = desc.querySelector('[data-bzc-desc-body]');
    const buttons = desc.querySelectorAll('[data-bzc-read-more]');
    if (!body || !buttons.length) return;

    const needsToggle = desc.getAttribute('data-needs-toggle') === 'true';
    if (!needsToggle) return;

    let collapsedHeight = 0;
    let animating = false;
    let endTimer = null;

    function getCollapsedHeight() {
      const styles = window.getComputedStyle(body);
      const lineHeight = parseFloat(styles.lineHeight) || 22;
      const lines =
        Number(getComputedStyle(desc).getPropertyValue('--bzc-desc-lines')) || 3;
      return Math.ceil(lineHeight * lines);
    }

    function finish(expand) {
      if (endTimer) {
        window.clearTimeout(endTimer);
        endTimer = null;
      }

      if (expand) {
        body.style.maxHeight = 'none';
        desc.classList.remove('is-collapsed');
      } else {
        desc.classList.add('is-collapsed');
        body.style.maxHeight = (collapsedHeight || getCollapsedHeight()) + 'px';
      }
      animating = false;
    }

    function setExpanded(expand) {
      if (animating) return;
      animating = true;

      if (endTimer) window.clearTimeout(endTimer);

      if (expand) {
        const startHeight =
          body.getBoundingClientRect().height || collapsedHeight || getCollapsedHeight();
        desc.classList.remove('is-collapsed');
        body.style.maxHeight = 'none';
        const fullHeight = body.scrollHeight;
        body.style.maxHeight = startHeight + 'px';
        void body.offsetHeight;
        body.style.maxHeight = fullHeight + 'px';

        body.addEventListener('transitionend', function handler(e) {
          if (e.target !== body) return;
          if (e.propertyName && e.propertyName !== 'max-height') return;
          body.removeEventListener('transitionend', handler);
          finish(true);
        });
        endTimer = window.setTimeout(function () {
          finish(true);
        }, 450);

        window.setTimeout(function () {
          try {
            desc.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } catch (err) {}
        }, 40);
      } else {
        desc.classList.remove('is-collapsed');
        body.style.maxHeight = 'none';
        const fullHeight = body.scrollHeight;
        const target = collapsedHeight || getCollapsedHeight();

        body.style.maxHeight = fullHeight + 'px';
        void body.offsetHeight;
        body.style.maxHeight = target + 'px';

        body.addEventListener('transitionend', function handler(e) {
          if (e.target !== body) return;
          if (e.propertyName && e.propertyName !== 'max-height') return;
          body.removeEventListener('transitionend', handler);
          finish(false);
        });
        endTimer = window.setTimeout(function () {
          finish(false);
        }, 450);

        window.setTimeout(function () {
          try {
            desc.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } catch (err) {}
        }, 40);
      }
    }

    requestAnimationFrame(function () {
      collapsedHeight = getCollapsedHeight();
      const isOverflowing = body.scrollHeight > body.clientHeight + 2;
      desc.setAttribute('data-needs-toggle', isOverflowing ? 'true' : 'false');

      if (desc.classList.contains('is-collapsed') && isOverflowing) {
        body.style.maxHeight = collapsedHeight + 'px';
      }
    });

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const expand = btn.getAttribute('data-mode') === 'more';
        setExpanded(expand);
      });
    });
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-bzc-gallery]');
    const thumbs = root.querySelectorAll('[data-bzc-thumb]');
    const mainImg = root.querySelector('[data-bzc-main-image]');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const img = thumb.querySelector('img');
        if (!img && !thumb.getAttribute('data-full-url')) return;

        // Prefer Liquid-provided full URL (Shopify CDN safe)
        let large = thumb.getAttribute('data-full-url') || '';
        if (!large && img) {
          const raw = img.currentSrc || img.src || '';
          // Modern CDN: ?width=160 -> ?width=1200
          if (/([?&])width=\d+/i.test(raw)) {
            large = raw.replace(/([?&])width=\d+/i, '$1width=1200');
          } else if (/_\d+x(?=\.|$)/.test(raw)) {
            // Legacy: _160x -> _1200x
            large = raw.replace(/_\d+x(?=\.|$)/, '_1200x');
          } else {
            large = raw;
          }
        }

        if (!large) return;

        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = large;
        if (img) mainImg.alt = img.alt || mainImg.alt;

        // Build a proper large srcset from the full URL
        if (large.indexOf('width=') !== -1) {
          const widths = [400, 600, 800, 1000, 1200];
          mainImg.srcset = widths
            .map(function (w) {
              return large.replace(/([?&])width=\d+/i, '$1width=' + w) + ' ' + w + 'w';
            })
            .join(', ');
          mainImg.sizes = '(min-width: 900px) 45vw, 100vw';
        }

        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');
      });
    });

    const nav = gallery && gallery.querySelector('[data-bzc-thumbs-nav]');
    const track = gallery && gallery.querySelector('[data-bzc-thumbs]');
    const prevBtn = gallery && gallery.querySelector('[data-bzc-thumbs-prev]');
    const nextBtn = gallery && gallery.querySelector('[data-bzc-thumbs-next]');
    if (!nav || !track || !prevBtn || !nextBtn) return;

    function isHorizontal() {
      return window.getComputedStyle(track).flexDirection === 'row';
    }

    function getStep() {
      const thumb = track.querySelector('[data-bzc-thumb]');
      if (!thumb) return 80;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.gap || styles.rowGap || styles.columnGap) || 0;
      if (isHorizontal()) {
        return thumb.getBoundingClientRect().width + gap;
      }
      return thumb.getBoundingClientRect().height + gap;
    }

    function updateArrows() {
      const horizontal = isHorizontal();
      const max = horizontal
        ? track.scrollWidth - track.clientWidth
        : track.scrollHeight - track.clientHeight;
      const pos = horizontal ? track.scrollLeft : track.scrollTop;
      const eps = 2;
      const canScroll = max > eps || thumbs.length > 5;

      nav.classList.toggle('has-overflow', canScroll);
      prevBtn.hidden = false;
      nextBtn.hidden = false;
      prevBtn.disabled = max <= eps || pos <= eps;
      nextBtn.disabled = max <= eps || pos >= max - eps;
    }

    function scrollByDir(dir) {
      const step = getStep() || 90;
      if (isHorizontal()) {
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      } else {
        track.scrollBy({ top: dir * step, behavior: 'smooth' });
      }
      window.setTimeout(updateArrows, 320);
    }

    prevBtn.addEventListener('click', function () {
      scrollByDir(-1);
    });
    nextBtn.addEventListener('click', function () {
      scrollByDir(1);
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    // Wait for images so overflow height is correct
    const imgs = track.querySelectorAll('img');
    let pending = imgs.length;
    function done() {
      updateArrows();
    }
    if (!pending) {
      requestAnimationFrame(done);
    } else {
      imgs.forEach(function (img) {
        if (img.complete) {
          pending -= 1;
          if (pending <= 0) done();
        } else {
          img.addEventListener(
            'load',
            function () {
              pending -= 1;
              if (pending <= 0) done();
            },
            { once: true }
          );
          img.addEventListener(
            'error',
            function () {
              pending -= 1;
              if (pending <= 0) done();
            },
            { once: true }
          );
        }
      });
      window.setTimeout(done, 600);
    }
    requestAnimationFrame(updateArrows);
  }

  function initQty(root) {
    const wrap = root.querySelector('[data-bzc-qty]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-bzc-qty-input]');
    const minus = wrap.querySelector('[data-bzc-qty-minus]');
    const plus = wrap.querySelector('[data-bzc-qty-plus]');
    if (!input) return;

    function setQty(next) {
      const min = Number(input.min || 1);
      const value = Math.max(min, Number(next) || min);
      input.value = String(value);
    }

    if (minus) {
      minus.addEventListener('click', function () {
        setQty(Number(input.value) - 1);
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        setQty(Number(input.value) + 1);
      });
    }
    input.addEventListener('change', function () {
      setQty(input.value);
    });
  }

  function initVariants(root, product) {
    const selects = root.querySelectorAll('[data-bzc-option]');
    if (!product || !selects.length) return;

    const variantInput = root.querySelector('[data-bzc-variant-id]');
    const atc = root.querySelector('[data-bzc-atc]');
    const priceEl = root.querySelector('[data-bzc-price-current]');
    const compareEl = root.querySelector('[data-bzc-price-compare]');
    const mainImg = root.querySelector('[data-bzc-main-image]');

    function sync() {
      const selected = Array.from(selects).map(function (s) {
        return s.value;
      });
      const variant = getVariantFromOptions(product, selected);
      if (!variant) return;

      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
      }

      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (atc) {
        atc.disabled = !variant.available;
        atc.removeAttribute('aria-disabled');
        const label = atc.querySelector('[data-bzc-atc-label]') || atc.querySelector('span');
        const labelText = variant.available
          ? atc.dataset.labelAvailable || 'Add To Cart'
          : atc.dataset.labelSoldout || 'Sold out';
        if (label) {
          label.textContent = labelText;
        } else {
          atc.textContent = labelText;
        }
      }

      if (mainImg && variant.featured_image) {
        mainImg.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_1200x$1$2');
        mainImg.alt = variant.featured_image.alt || product.title;
      }

      if (variant.id && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    if (atc) {
      const label = atc.querySelector('[data-bzc-atc-label]');
      atc.dataset.labelAvailable = (label ? label.textContent : atc.textContent).trim();
      atc.dataset.labelSoldout = 'Sold out';
    }

    selects.forEach(function (select) {
      select.addEventListener('change', sync);
    });
  }

  function initProductForm(root) {
    const form = root.querySelector('[data-bzc-product-form]');
    const errorEl = root.querySelector('[data-bzc-form-error]');
    if (!form) return;

    // Theme product-form.js owns ATC when <product-form> is present.
    // Do not attach a second submit handler — it breaks cart drawer updates.
    if (form.closest('product-form')) {
      return;
    }

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      const atc = form.querySelector('[data-bzc-atc]');
      const qtyInput = form.querySelector('[data-bzc-qty-input]');
      const submitter = event.submitter || atc;

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      if (atc) atc.disabled = true;
      if (submitter && submitter !== atc) submitter.disabled = true;

      try {
        const formData = new FormData(form);
        if (!formData.get('id')) {
          throw new Error('Please select an available variant');
        }
        if (!formData.has('quantity')) {
          formData.append('quantity', String(Math.max(1, Number(qtyInput && qtyInput.value) || 1)));
        }
        const response = await addToCartFromFormData(formData);
        publishCartUpdate(response);
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || 'Error adding to cart';
          errorEl.hidden = false;
        }
      } finally {
        if (atc && !atc.dataset.forceDisabled) atc.disabled = false;
        if (submitter && submitter !== atc) submitter.disabled = false;
      }
    });
  }

  function initBundles(root) {
    const buttons = root.querySelectorAll('[data-bzc-bundle-add]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', async function () {
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        const defaultLabel = btn.getAttribute('data-label-default') || '+ ADD';
        const addedLabel = btn.getAttribute('data-label-added') || 'Added';

        btn.disabled = true;
        try {
          const response = await addVariantToCart(variantId, 1);
          btn.classList.add('is-added');
          btn.textContent = addedLabel;
          publishCartUpdate(response);

          window.setTimeout(function () {
            btn.classList.remove('is-added');
            btn.textContent = defaultLabel;
            btn.disabled = false;
          }, 1600);
        } catch (err) {
          btn.disabled = false;
          window.alert(err.message || 'Could not add item');
        }
      });
    });
  }

  function initProductSection(root) {
    if (!root || root.dataset.bzcInit === 'true') return;
    root.dataset.bzcInit = 'true';

    const product = parseProductJson(root);
    initReadMore(root);
    initGallery(root);
    initQty(root);
    initVariants(root, product);
    initProductForm(root);
    initBundles(root);
  }

  function boot() {
    document.querySelectorAll('[data-bzc-product]').forEach(initProductSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target && event.target.querySelector
      ? event.target.querySelector('[data-bzc-product]')
      : null;
    if (root) {
      root.dataset.bzcInit = 'false';
      initProductSection(root);
    } else if (event.target && event.target.matches && event.target.matches('[data-bzc-product]')) {
      event.target.dataset.bzcInit = 'false';
      initProductSection(event.target);
    }
  });
})();
